import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/**
 * The button primitive — BUILD-PLAN §44/§45.
 *
 * Restrained by design (§5 warns against heavy rounding and gradients) and
 * accessible by construction: a minimum 44px target height per §27/WCAG 2.2,
 * and a busy state that announces itself rather than only spinning.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-ink text-white hover:opacity-90 disabled:hover:opacity-100',
  secondary:
    'border border-ink text-ink hover:bg-ink hover:text-white disabled:hover:bg-transparent disabled:hover:text-ink',
  ghost: 'text-ink hover:text-accent',
  danger: 'bg-danger text-white hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  sm: 'min-h-9 px-4 text-xs',
  md: 'min-h-11 px-7 text-sm',
  lg: 'min-h-12 px-10 text-sm',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-sm font-medium tracking-wide transition-all disabled:cursor-not-allowed disabled:opacity-50';

interface BaseProps {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
}

interface ButtonProps extends BaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  /** Shows a spinner and blocks further clicks. */
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  children,
  className = '',
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      // Defaults to "button": an unlabelled <button> inside a form submits it,
      // which is a classic source of accidental double submissions.
      type={type}
      // Disabled while loading so a slow network cannot produce a duplicate
      // order or a second registration (§27).
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[BASE, VARIANTS[variant], SIZES[size], fullWidth ? 'w-full' : '', className]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
      {children}
    </button>
  );
}

interface ButtonLinkProps extends BaseProps {
  to: string;
  /**
   * Leaves the app — `mailto:`, `tel:` or another origin. Router `<Link>`
   * treats those as in-app paths and produces a link that goes nowhere, so
   * they must render a plain anchor instead.
   */
  external?: boolean;
  /**
   * Fired on navigation — for tidying up before leaving, such as closing a
   * drawer that would otherwise stay open over the destination.
   */
  onClick?: () => void;
}

/** Same visual treatment for navigation, which must remain a real link. */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  external = false,
  onClick,
  children,
  className = '',
}: ButtonLinkProps) {
  const classes = [BASE, VARIANTS[variant], SIZES[size], fullWidth ? 'w-full' : '', className]
    .filter(Boolean)
    .join(' ');

  if (external) {
    /*
     | Only http(s) targets open a tab and need the opener severed; mailto and
     | tel hand off to the OS and must stay in place.
     */
    const isWeb = /^https?:/i.test(to);

    return (
      <a
        href={to}
        className={classes}
        onClick={onClick}
        {...(isWeb ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={classes} onClick={onClick}>
      {children}
    </Link>
  );
}
